import { useEffect, useState, useContext } from "react";
import { Table, Button, Modal, Input, Space, Tag, DatePicker } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { AuthContext } from "../../context/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const { TextArea } = Input;

export default function ApproveTI() {
    const { authData, backUrl } = useContext(AuthContext);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const [remarks, setRemarks] = useState("");
    const [actionType, setActionType] = useState("");
    const [modalOpen, setModalOpen] = useState(false);

    // 🔹 Date filters
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    // 🔹 Fetch Technical Issues with filters
    const fetchIssues = async () => {
        try {
            setLoading(true);

            const params = {};
            if (fromDate) params.from_date = fromDate.format("YYYY-MM-DD");
            if (toDate) params.to_date = toDate.format("YYYY-MM-DD");

            const res = await axios.get(
                `${backUrl}/api/hr/technical-issues/`,
                {
                    params,
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );

            const responseData = res.data;

            if (Array.isArray(responseData)) {
                setData(responseData);
            } else if (Array.isArray(responseData.results)) {
                setData(responseData.results);
            } else {
                setData([]);
            }

        } catch (error) {
            toast.error("Failed to load technical issues");
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    // 🔹 Initial load + filter change
    useEffect(() => {
        fetchIssues();
    }, [fromDate, toDate]);

    // 🔹 Approve / Reject API
    const handleSubmit = async () => {
        if (!remarks.trim()) {
            toast.warning("Please enter remarks");
            return;
        }

        const url =
            actionType === "approve"
                ? `${backUrl}/api/hr/technical-issues/approve-issues/`
                : `${backUrl}/api/hr/technical-issues/reject-issues/`;

        try {
            setLoading(true);

            await axios.post(
                url,
                {
                    issue_ids: selectedRowKeys,
                    remarks,
                },
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );

            toast.success(
                actionType === "approve"
                    ? "Issues approved successfully"
                    : "Issues rejected successfully"
            );

            setModalOpen(false);
            setSelectedRowKeys([]);
            setRemarks("");
            fetchIssues();
        } catch (error) {
            toast.error("Action failed");
        } finally {
            setLoading(false);
        }
    };

    // 🔹 Table columns
    const columns = [
        {
            title: "ID",
            dataIndex: "id",
        },
        {
            title: "Title",
            dataIndex: "title",
        },
        {
            title: "Reason",
            dataIndex: "reason",
        },
        {
            title: "Date",
            dataIndex: "date",
            render: (date) => dayjs(date).format("DD-MM-YYYY"),
        },
        {
            title: "Status",
            dataIndex: "status",
        },
        {
            title: "Remarks",
            dataIndex: "approved_remarks",
        }
    ];

    return (
        <>
            {/* 🔍 Filters */}
            <Space style={{ marginBottom: 16 }}>
                <DatePicker
                    placeholder="From Date"
                    value={fromDate}
                    onChange={(date) => setFromDate(date)}
                />

                <DatePicker
                    placeholder="To Date"
                    value={toDate}
                    onChange={(date) => setToDate(date)}
                />

                <Button
                    onClick={() => {
                        setFromDate(null);
                        setToDate(null);
                    }}
                >
                    Clear
                </Button>
            </Space>

            {/* 🔐 Admin Actions */}
            {authData?.employee?.is_superuser && (
                <Space style={{ marginBottom: 16, marginLeft: 10 }}>
                    <Button
                        type="primary"
                        disabled={!selectedRowKeys.length}
                        onClick={() => {
                            setActionType("approve");
                            setModalOpen(true);
                        }}
                        style={{ background: "green", color: "white" }}
                    >
                        Approve
                    </Button>

                    <Button
                        disabled={!selectedRowKeys.length}
                        onClick={() => {
                            setActionType("reject");
                            setModalOpen(true);
                        }}
                        style={{ background: "red", color: "white" }}
                    >
                        Reject
                    </Button>
                </Space>
            )}

            <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={data}
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys,
                }}
                pagination={{ pageSize: 10 }}
                bordered
                components={{
                    header: {
                        cell: (props) => (
                            <th
                                {...props}
                                style={{
                                    backgroundColor: "#0e497b",
                                    color: "white",
                                }}
                            >
                                {props.children}
                            </th>
                        ),
                    },
                }}
            />

            {/* 📝 Remarks Modal */}
            <Modal
                title={
                    actionType === "approve"
                        ? "Approve Technical Issues"
                        : "Reject Technical Issues"
                }
                open={modalOpen}
                onOk={handleSubmit}
                onCancel={() => setModalOpen(false)}
                confirmLoading={loading}
            >
                <TextArea
                    rows={4}
                    placeholder="Enter remarks"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                />
            </Modal>

            <ToastContainer position="top-right" autoClose={3000} />
        </>
    );
}
